// ============================================================================
// LE RENDU D'UNE ÉTIQUETTE — une planche, ses éléments, ses barres.
//
// UTILITÉ : la MÊME étiquette doit se dessiner dans trois contextes — la vignette d'un gabarit,
// la planche de l'éditeur, et la page à imprimer. Un rendu par contexte, ce sont trois étiquettes
// qui finissent par différer, et c'est celle qu'on n'a pas regardée qui part à l'imprimante.
//
// ⚠️ RIEN NE SE CALCULE ICI. Largeur d'un élément, qualité d'un code-barres, tracé des barres :
// tout vient de `@orgazone/core-catalog`. Ce fichier POSITIONNE, il ne décide pas.
//
// ⚠️ L'ÉCHELLE EST UN PARAMÈTRE, PAS UNE CONSTANTE. `scale` = pixels par millimètre. À l'écran c'est
// le zoom ; à l'impression ce sera l'unité `mm` du navigateur. Le même composant sert les deux parce
// qu'il ne connaît que des millimètres.
// ============================================================================
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { ean13Runs, elementWidthMm, FIELD_BARCODE, type LabelElement, type LabelFieldKey, type LabelTemplate } from '@orgazone/core-catalog';
import { formatMoney } from '@orgazone/money-format';
import type { LabelSampleRow } from '../api';
import { t } from '../i18n';

/** Ce que porte une étiquette : la valeur de chaque champ, déjà mise en forme. */
export type LabelValues = Partial<Record<string, string>>;

/**
 * LES VALEURS D'UNE ÉTIQUETTE POUR UNE DÉCLINAISON LUE AU CATALOGUE — l'aperçu, le tirage d'essai et les deux
 * impressions en série passent par ici.
 * ⚠️ UNE SEULE CORRESPONDANCE : recopiée dans chaque écran, un champ ajouté au cœur s'imprimait dans l'un et restait vide
 *    dans l'autre. Le type la tient : il réclame chaque champ que le cœur déclare.
 * ⚠️ LE PRIX SE MET EN FORME ICI, à l'affichage : le serveur le rend en centimes, la devise est celle du magasin.
 */
export function labelValuesOf(row: LabelSampleRow): Record<LabelFieldKey, string> {
  return {
    desig: row.desig, code: row.code, price: formatMoney(row.priceCents), barcode: row.barcode, gencode: row.gencode,
    size: row.size, color: row.color, brand: row.brand, sup: row.sup, origin: row.origin, season: row.season,
  };
}

/**
 * LES BARRES D'UN EAN-13, en dégradé CSS.
 *
 * ⚠️ UN DÉGRADÉ PLUTÔT QUE 60 DIVS : c'est une seule propriété, que le navigateur rend à l'échelle
 * exacte demandée — là où des éléments arrondis au pixel décaleraient les barres les unes par
 * rapport aux autres à l'impression. C'est la technique de la maquette, et elle est juste.
 * ⚠️ SANS CODE VALIDE, ON MONTRE UNE TRAME GRISE, jamais un faux code-barres : l'opérateur doit voir
 * qu'il n'y a rien à scanner ici (§23.0).
 */
export function barcodeBackground(code: string): string {
  const runs = ean13Runs(code);
  if (!runs) return 'repeating-linear-gradient(90deg,var(--ink-300) 0 2px,transparent 2px 4px)';
  const stops = runs
    .map((r) => {
      const color = r.bar ? 'var(--ink-900)' : 'transparent';
      return `${color} ${(r.from * 100).toFixed(4)}% ${(r.to * 100).toFixed(4)}%`;
    })
    .join(',');
  return `linear-gradient(90deg,${stops})`;
}

/**
 * CE CHAMP N'A RIEN À IMPRIMER POUR CES VALEURS — la règle unique du fantôme de l'éditeur, du compte « N champs vides » et
 * de la pastille du panneau.
 * ⚠️ LE CODE-BARRES N'EN FAIT PAS PARTIE : sans code, il montre déjà sa trame grise, à sa vraie taille.
 */
export function isEmptyOnLabel(el: LabelElement, values: LabelValues): boolean {
  return el.field !== FIELD_BARCODE && !(values[el.field] ?? '').trim();
}

/** Le style d'UN élément posé, à une échelle donnée. */
function elementStyle(el: LabelElement, scale: number): CSSProperties {
  const barcode = el.field === FIELD_BARCODE;
  const boxed = !barcode && el.widthMm !== undefined;
  return {
    position: 'absolute',
    left: `${el.xMm * scale}px`,
    top: `${el.yMm * scale}px`,
    // ⚠️ PLANCHER À 5 px : sous cette taille le navigateur ne rend plus rien de lisible, et un
    //    élément invisible dans l'éditeur devient impossible à rattraper à la souris.
    fontSize: `${Math.max(5, el.sizeMm * scale)}px`,
    fontWeight: el.bold ? 700 : 400,
    lineHeight: 1.05,
    whiteSpace: 'nowrap',
    color: 'var(--ink-900)',
    padding: '1px 2px',
    borderRadius: 'var(--r-r2)',
    width: boxed ? `${el.widthMm! * scale}px` : 'auto',
    overflow: boxed ? 'hidden' : 'visible',
    textOverflow: 'ellipsis',
    textAlign: el.align,
    fontFamily: barcode ? 'var(--font-mono)' : 'inherit',
  };
}

export interface LabelPlateProps {
  template: LabelTemplate;
  values: LabelValues;
  /** Pixels par millimètre. */
  scale: number;
  /** Index de l'élément sélectionné — l'éditeur seul s'en sert. */
  selected?: number | null;
  /**
   * Prise en main d'un élément (glisser), avec ce qu'il faut pour le CHOISIR AU CLAVIER : son nom et le choix lui-même.
   * Absent = planche non interactive.
   * ⚠️ LES TROIS VONT ENSEMBLE : un élément qu'on ne prend qu'au pointeur ne se règle pas au clavier — le panneau de
   *    propriétés ne s'ouvrait que pour l'élément qu'on venait d'AJOUTER.
   */
  interaction?: {
    onGrab: (index: number, e: ReactPointerEvent<HTMLDivElement>) => void;
    onSelect: (index: number) => void;
    nameOf: (field: LabelFieldKey) => string;
  };
  /** Prise en main de la poignée de redimensionnement. */
  onResize?: (index: number, e: ReactPointerEvent<HTMLSpanElement>) => void;
  /** Clic hors élément — désélectionne. */
  onBlank?: () => void;
  style?: CSSProperties;
}

/**
 * UNE ÉTIQUETTE DESSINÉE À L'ÉCHELLE.
 * ⚠️ Le même composant sert la vignette (échelle 1,2), la planche (échelle 4 à 12) et l'impression.
 * Sans `interaction`, il est purement décoratif — et ne capte aucun clic.
 */
export function LabelPlate({ template, values, scale, selected = null, interaction, onResize, onBlank, style }: LabelPlateProps) {
  const interactive = !!interaction;
  return (
    <div
      onPointerDown={onBlank ? (e) => { if (e.target === e.currentTarget) onBlank(); } : undefined}
      style={{
        position: 'relative',
        width: `${template.widthMm * scale}px`,
        height: `${template.heightMm * scale}px`,
        background: 'var(--paper)',
        border: '1px solid var(--ink-200)',
        borderRadius: 'var(--r-r2)',
        overflow: 'hidden',
        flexShrink: 0,
        ...style,
      }}
    >
      {template.elements.map((el, i) => {
        const isSelected = interactive && selected === i;
        const barcode = el.field === FIELD_BARCODE;
        const text = barcode ? '' : (values[el.field] ?? '');
        // ⚠️ LE FANTÔME D'UN CHAMP VIDE — dans l'éditeur seulement. Un texte vide se réduisait à une boîte de quelques
        //    pixels, sans contour hors sélection : l'élément était sur la planche, invisible et presque impossible à
        //    attraper. Il montre donc le NOM de son champ, grisé, dans un pointillé, à la hauteur de son corps.
        // ⚠️ JAMAIS HORS DE L'ÉDITEUR : une vignette ou un aperçu d'impression montrent ce qui SORTIRA, et un champ vide
        //    ne sort pas. Le nom d'un champ imprimé sur une étiquette irait en rayon tel quel.
        const ghost = interactive && isEmptyOnLabel(el, values);
        const fontPx = Math.max(5, el.sizeMm * scale);
        return (
          <div
            key={`${el.field}-${i}`}
            onPointerDown={interaction ? (e) => interaction.onGrab(i, e) : undefined}
            // ⚠️ UN ÉLÉMENT DE LA PLANCHE EST UN BOUTON : il s'atteint à la tabulation, se nomme par son champ, dit s'il est
            //    choisi, et Entrée ou Espace le choisit — comme le clic.
            role={interaction ? 'button' : undefined}
            tabIndex={interaction ? 0 : undefined}
            aria-label={interaction ? interaction.nameOf(el.field) : undefined}
            aria-pressed={interaction ? isSelected : undefined}
            onKeyDown={interaction ? (e) => {
              // ⚠️ ÉCHAP DÉSÉLECTIONNE, comme un clic dans le vide de la planche — le seul chemin clavier de ce geste.
              if (e.key === 'Escape' && onBlank) { e.preventDefault(); onBlank(); return; }
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              interaction.onSelect(i);
            } : undefined}
            style={{
              ...elementStyle(el, scale),
              cursor: interactive ? 'grab' : 'default',
              touchAction: interactive ? 'none' : undefined,
              // ⚠️ UN PLANCHER DANS L'ÉDITEUR : la hauteur d'une ligne au corps réglé, jamais écrasée à zéro, et de quoi poser
              //    le pointeur. Il porte sur le CONTENU, comme la largeur d'une boîte : un `border-box` rétrécirait de son
              //    rembourrage la boîte d'un champ fixé, et l'éditeur couperait un texte que l'impression laisse entier.
              ...(interactive ? { minHeight: `${Math.round(fontPx * 1.05)}px`, minWidth: 14 } : {}),
              ...(ghost ? { color: 'var(--ink-400)', fontStyle: 'italic' as const } : {}),
              outline: isSelected ? '1.5px solid var(--teal-500)' : ghost ? '1px dashed var(--ink-300)' : '1.5px solid transparent',
              outlineOffset: 1,
              background: isSelected ? 'rgba(43,201,163,.10)' : 'transparent',
            }}
          >
            {barcode ? (
              <span
                style={{
                  display: 'block',
                  width: `${elementWidthMm(el) * scale}px`,
                  height: `${el.sizeMm * scale}px`,
                  background: barcodeBackground(values.barcode ?? ''),
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '100% 100%',
                }}
              />
            ) : ghost && interaction ? interaction.nameOf(el.field) : text}
            {isSelected && onResize && (
              <span
                onPointerDown={(e) => { e.stopPropagation(); onResize(i, e); }}
                // ⚠️ UNE COMMODITÉ DU POINTEUR, CACHÉE AUX AIDES TECHNIQUES : la taille se règle aussi par les champs du
                //    panneau, qui s'atteignent au clavier ; un nom posé sur un `span` sans rôle n'existe pas.
                aria-hidden="true"
                title={t('Redimensionner')}
                style={{
                  position: 'absolute', right: -6, bottom: -6, width: 14, height: 14,
                  borderRadius: 'var(--r-xs)', background: 'var(--teal-500)',
                  border: '2px solid var(--paper)', cursor: 'nwse-resize', touchAction: 'none',
                  boxShadow: '0 1px 4px rgba(12,14,16,.3)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
